import React from "react";

export type Language = "ko" | "en";

export type LocalizedText = {
  ko: string;
  en: string;
};

type LanguageApi = {
  language: Language;
  setLanguage: (language: Language) => void;
  toggleLanguage: () => void;
  t: (text: LocalizedText | string, fallbackEn?: string) => string;
};

const STORAGE_KEY = "itc-language";
const LanguageContext = React.createContext<LanguageApi | null>(null);

export function pickLanguage(language: Language, text: LocalizedText | string, fallbackEn?: string) {
  if (typeof text === "string") {
    return language === "en" && fallbackEn ? fallbackEn : text;
  }
  return text[language];
}

function applyLanguage(language: Language) {
  document.documentElement.lang = language === "ko" ? "ko" : "en";
  document.documentElement.dataset.lang = language;
}

function readInitial(): Language {
  try {
    const saved = window.localStorage.getItem(STORAGE_KEY);
    return saved === "en" || saved === "ko" ? saved : "ko";
  } catch {
    return "ko";
  }
}

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [language, setLanguageState] = React.useState<Language>(readInitial);

  React.useEffect(() => {
    applyLanguage(language);
  }, [language]);

  const persist = React.useCallback((nextLanguage: Language) => {
    applyLanguage(nextLanguage);
    try {
      window.localStorage.setItem(STORAGE_KEY, nextLanguage);
    } catch {
      /* Ignore storage restrictions. */
    }
  }, []);

  const setLanguage = React.useCallback(
    (nextLanguage: Language) => {
      setLanguageState(nextLanguage);
      persist(nextLanguage);
    },
    [persist]
  );

  const toggleLanguage = React.useCallback(() => {
    setLanguageState((current) => {
      const nextLanguage: Language = current === "ko" ? "en" : "ko";
      persist(nextLanguage);
      return nextLanguage;
    });
  }, [persist]);

  const t = React.useCallback(
    (text: LocalizedText | string, fallbackEn?: string) => pickLanguage(language, text, fallbackEn),
    [language]
  );

  const api = React.useMemo(
    () => ({ language, setLanguage, toggleLanguage, t }),
    [language, setLanguage, toggleLanguage, t]
  );

  return <LanguageContext.Provider value={api}>{children}</LanguageContext.Provider>;
}

export function useLanguage(): LanguageApi {
  const context = React.useContext(LanguageContext);
  if (context) return context;

  const current = (document.documentElement.dataset.lang as Language) || "ko";
  return {
    language: current,
    setLanguage: applyLanguage,
    toggleLanguage: () => applyLanguage(current === "ko" ? "en" : "ko"),
    t: (text, fallbackEn) => pickLanguage(current, text, fallbackEn),
  };
}

/**
 * 이 아래 화면만 언어를 고정한다(전역 설정은 그대로) — 예: 단계 이동 모달의 샘플 진행은 영문으로.
 *   언어 바꾸기는 바깥(전역) 설정으로 넘긴다.
 */
export function LanguageOverride({ language, children }: { language: Language; children: React.ReactNode }) {
  const outer = useLanguage();
  const api = React.useMemo<LanguageApi>(() => ({
    language,
    setLanguage: outer.setLanguage,
    toggleLanguage: outer.toggleLanguage,
    t: (text, fallbackEn) => pickLanguage(language, text, fallbackEn),
  }), [language, outer.setLanguage, outer.toggleLanguage]);
  return <LanguageContext.Provider value={api}>{children}</LanguageContext.Provider>;
}
